// Another person signed in on a device that still holds the previous user's unsent work: nothing
// of it is shown or sent in the new user's name (see session.ts).
import { useState } from 'react';
import type { User } from '../../../shared/types';
import { plural } from '../lib/format';
import { discardForeignData, type DeviceOwner, type LocalWork } from '../lib/session';
import { accountErrorText } from '../components/AccountForms';
import { confirmDialog } from '../components/Dialog';
import { Icon } from '../components/Icon';
import { confirmAndLogout } from '../components/Logout';
import { toast } from '../components/Toasts';

/** "1 neposlan posnetek, 2 neposlani spremembi in 1 še ne dodan ročni vnos v nadzoru" */
function workText(work: LocalWork): string {
  const parts: string[] = [];
  if (work.recordings > 0) parts.push(plural(work.recordings, ['neposlan posnetek', 'neposlana posnetka', 'neposlani posnetki', 'neposlanih posnetkov']));
  if (work.changes > 0) parts.push(plural(work.changes, ['neposlana sprememba', 'neposlani spremembi', 'neposlane spremembe', 'neposlanih sprememb']));
  if (work.drafts > 0) {
    parts.push(
      plural(work.drafts, ['še ne dodan ročni vnos v nadzoru', 'še ne dodana ročna vnosa v nadzorih', 'še ne dodani ročni vnosi v nadzorih', 'še ne dodanih ročnih vnosov v nadzorih']),
    );
  }
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} in ${parts.at(-1)}` : (parts[0] ?? '');
}

interface ForeignDataPageProps {
  user: User;
  previous: DeviceOwner;
  work: LocalWork;
  /** The data was discarded: check again and continue. */
  onResolved: () => void;
}

export function ForeignDataPage({ user, previous, work, onResolved }: ForeignDataPageProps) {
  const [busy, setBusy] = useState<'discard' | 'logout' | null>(null);
  const who = previous.name || 'prejšnjega uporabnika';

  const discard = async () => {
    const ok = await confirmDialog({
      title: 'Zavržem podatke?',
      message: `Iz te naprave bodo nepreklicno izbrisani podatki uporabnika ${who} (${workText(work)}). Na strežnik ne bodo poslani.`,
      confirmLabel: 'Zavrzi',
      danger: true,
    });
    if (!ok) return;
    setBusy('discard');
    try {
      await discardForeignData(user);
      toast('Podatki prejšnjega uporabnika so izbrisani iz te naprave.', { kind: 'success' });
      onResolved();
    } catch (err) {
      toast(accountErrorText(err, 'Podatkov ni bilo mogoče izbrisati.'), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const signOut = async () => {
    setBusy('logout');
    try {
      await confirmAndLogout();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="page page--center page--login">
      <section className="card login login--wide" aria-labelledby="foreign-title">
        <span className="login__icon login__icon--warning" aria-hidden="true">
          <Icon name="alert" size={28} />
        </span>
        <h1 id="foreign-title" className="login__title">
          V napravi so podatki drugega uporabnika
        </h1>
        <p>
          V tej napravi so podatki, ki jih je ustvaril uporabnik <strong>{who}</strong>: {workText(work)}. Zaradi zasebnosti jih ne prikažemo
          in jih ne pošljemo v vašem imenu.
        </p>
        <p className="muted">
          Če naj se pošljejo na strežnik, se odjavite, da se lahko {previous.name ? previous.name : 'prejšnji uporabnik'} prijavi v tej napravi.
          Lahko jih tudi zavržete – takrat bodo izgubljeni.
        </p>
        <button type="button" className="btn btn--primary btn--block" onClick={() => void signOut()} disabled={busy !== null}>
          <Icon name={busy === 'logout' ? 'spinner' : 'logout'} size={20} /> Odjava
        </button>
        <button type="button" className="btn btn--danger-ghost btn--block" onClick={() => void discard()} disabled={busy !== null}>
          <Icon name={busy === 'discard' ? 'spinner' : 'trash'} size={20} /> Zavrzi podatke in nadaljuj
        </button>
        <p className="muted small signed-in-as">
          Prijavljeni ste kot <strong>{user.displayName}</strong> ({user.username}).
        </p>
      </section>
    </div>
  );
}
